import { useSyncBrowserTimezoneToProfile } from '@cheqroom/hooks';
import { FC, useEffect } from 'react';

import useModal from '../../hooks/useModal';
import { TimezoneChangeModal } from '../TimezoneChangeModal/TimezoneChangeModal';

export type BrowserTimezoneSyncProps = {
	/**
	 * Authenticated user id. Doubles as the "profile is loaded" signal, so pass it from the
	 * same object `profileTimezone` comes from.
	 */
	userId?: string | null;
	/** Stored profile zone. Empty means "no zone stored" and is adopted silently. */
	profileTimezone?: string | null;
	/** Set false to skip entirely — unauthenticated, or impersonating someone else's profile. */
	enabled?: boolean;
	/** Optional side effect after a successful write (e.g. refreshing a legacy shell store). */
	onSynced?: (timezone: string) => void;
};

/**
 * Syncs the browser IANA timezone onto the user profile, showing the notice modal when the
 * offsets differ (legacy `timezone-modal.js` parity).
 *
 * Renders nothing itself — mount it once near the app root. Each app supplies its own auth
 * wiring; everything below that is identical between staff and portal, so it lives here.
 */
export const BrowserTimezoneSync: FC<BrowserTimezoneSyncProps> = ({
	userId,
	profileTimezone,
	enabled = true,
	onSynced,
}) => {
	const { pendingAck, confirmPendingAck } = useSyncBrowserTimezoneToProfile({
		userId,
		profileTimezone,
		enabled,
		onSynced,
	});

	const [showTimezoneModal, hideTimezoneModal] = useModal<{
		computerTimezone: string;
		profileTimezone: string;
	}>(({ computerTimezone, profileTimezone: profile, onClose }) => (
		<TimezoneChangeModal
			open
			computerTimezone={computerTimezone}
			profileTimezone={profile}
			onConfirm={() => {
				void confirmPendingAck().finally(() => {
					onClose();
				});
			}}
		/>
	));

	useEffect(() => {
		if (!pendingAck) {
			hideTimezoneModal();
			return;
		}

		showTimezoneModal({
			computerTimezone: pendingAck.browserTimezone,
			profileTimezone: pendingAck.profileTimezone,
		});
		// show/hide from useModal are unstable; only react to pendingAck
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
	}, [pendingAck]);

	return null;
};
